import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import YesNoRadioButton from "../../ui/YesNoRadioButton";
import "./OfficerCallOffSection.scss";
import { SettingsCard, RadioWithTooltip } from "../../ui";

const OfficerCallOffSection = props => {
	const {
		handleExpirationChange,
		handleGracePeriodChange,
		handleTypeChange,
		handleReplacementTypeChange,
		handleHasGracePeriodChange,
		id,
		sectionRef,
		title,
		subtitle,
		jobCallOffType,
		jobCallOffReplacementTypes,
		jobCallOffInitialTime,
		gracePeriodInitialTime,
		hasGracePeriod,
	} = props;
	const { lang } = global;
	const timePeriods = [
		{ maxValue: 60, factor: 1, label: "Minutes" },
		{ maxValue: 24, factor: 60, label: "Hours" },
		{ maxValue: 90, factor: 1440, label: "Days" },
	];
	const minTime = 0;
	const [maxTime, setMaxTime] = useState(60);
	const [maxGraceTime, setMaxGraceTime] = useState(60);
	const [currentTime, setCurrentTime] = useState(0);
	const [selectedPeriod, setSelectedPeriod] = useState(1);
	const [currentGraceTime, setCurrentGraceTime] = useState(0);
	const [selectedGraceTimePeriod, setSelectedGraceTimePeriod] = useState(1);

	useEffect(() => {
		if (jobCallOffInitialTime != null) {
			if (jobCallOffInitialTime < 60) {
				setCurrentTime(jobCallOffInitialTime);
			} else if (jobCallOffInitialTime >= 60 && jobCallOffInitialTime < 1440) {
				setSelectedPeriod(60);
				setMaxTime(24);
				setCurrentTime(jobCallOffInitialTime / 60);
			} else if (jobCallOffInitialTime >= 1440) {
				setSelectedPeriod(1440);
				setMaxTime(90);
				setCurrentTime(jobCallOffInitialTime / 1440);
			}
		}
	}, [jobCallOffInitialTime]);

	useEffect(() => {
		if (gracePeriodInitialTime != null) {
			if (gracePeriodInitialTime < 60) {
				setCurrentGraceTime(gracePeriodInitialTime);
			} else if (gracePeriodInitialTime >= 60 && gracePeriodInitialTime < 1440) {
				setSelectedGraceTimePeriod(60);
				setMaxGraceTime(24);
				setCurrentGraceTime(gracePeriodInitialTime / 60);
			} else if (gracePeriodInitialTime >= 1440) {
				setSelectedGraceTimePeriod(1440);
				setMaxGraceTime(90);
				setCurrentGraceTime(gracePeriodInitialTime / 1440);
			}
		}
	}, [gracePeriodInitialTime]);

	useEffect(() => {
		let maxVal = 0;
		if (hasGracePeriod && (jobCallOffType === 2 || jobCallOffType === "")) {
			if (Number(selectedPeriod) === Number(selectedGraceTimePeriod)) {
				maxVal = Number(currentGraceTime);
			} else if (
				Number(selectedPeriod) < Number(selectedGraceTimePeriod) &&
				currentGraceTime !== 0
			) {
				maxVal = timePeriods.find(unit => unit.factor === Number(selectedPeriod)).maxValue;
			}
		} else {
			maxVal = timePeriods.find(unit => unit.factor === Number(selectedPeriod)).maxValue;
		}
		setMaxTime(maxVal);
	}, [hasGracePeriod, jobCallOffType, selectedPeriod, selectedGraceTimePeriod, currentGraceTime]);

	const unitOfTimeChanged = event => {
		setSelectedPeriod(event.target.value);
		setCurrentTime(0);
		handleExpirationChange(0);
	};

	const unitOfTimeGracePeriodChanged = event => {
		const maxVal = timePeriods.find(
			unit => unit.factor === Number(event.target.value),
		).maxValue;
		setMaxGraceTime(maxVal);
		setSelectedGraceTimePeriod(event.target.value);
		setCurrentGraceTime(0);
		handleGracePeriodChange(0);
	};

	const graceTimeChanged = event => {
		if (Number.isInteger(Number(event.target.value))) {
			const value = Math.max(minTime, Math.min(maxGraceTime, Number(event.target.value)));
			setCurrentGraceTime(value);
			handleGracePeriodChange(value * Number(selectedGraceTimePeriod));
		}
	};

	const expirationTimeChanged = event => {
		if (Number.isInteger(Number(event.target.value))) {
			const value = Math.max(minTime, Math.min(maxTime, Number(event.target.value)));
			setCurrentTime(value);
			handleExpirationChange(value * Number(selectedPeriod));
		}
	};

	return (
		<SettingsCard
			id={id}
			sectionRef={sectionRef}
			title={title}
			subtitle={subtitle}
		>
			<div className="standard-padding row">
				<div className="col-xs-12">
					{lang.t("How would you like the officers to call off_")}
				</div>
			</div>

			<div className="standard-padding row">
				<RadioWithTooltip
					id="call-off"
					name="call-off-options"
					handleChange={handleTypeChange("")}
					checked={jobCallOffType === ""}
					label={lang.t("Officer must call coordinator")}
					tooltipMessage={lang.t(
						"The officer will be required to call a coordinator in order to call off_",
					)}
				/>
				<RadioWithTooltip
					id="call-off-2"
					name="call-off-options"
					handleChange={handleTypeChange(2)}
					checked={jobCallOffType === 2}
					label={lang.t("Officer must find a replacement")}
					tooltipMessage={lang.t(
						"This will allow the officer to be removed from the job only if a replacement is found_",
					)}
				/>
				<If condition={jobCallOffType === 2}>
					<div className="row form-group">
						<div className="col-xs-12 col-sm-11 subcategory-margin">
							{lang.t("How would you like the officers to request replacement_")}
						</div>
						<div className="col-xs-10 col-xs-offset-2 col-sm-11 subcategory-margin checkbox">
							<label htmlFor="selectReplacement">
								<input
									type="checkbox"
									name="selectReplacement"
									id="selectReplacement"
									value={1}
									onChange={handleReplacementTypeChange}
									checked={jobCallOffReplacementTypes.includes(1)}
								/>
								{lang.t("Search and select a specific replacement")}
							</label>
						</div>
						<div className="col-xs-10 col-xs-offset-2 col-sm-11 subcategory-margin checkbox">
							<label htmlFor="repostJob">
								<input
									type="checkbox"
									name="repostJob"
									id="repostJob"
									value={2}
									onChange={handleReplacementTypeChange}
									checked={jobCallOffReplacementTypes.includes(2)}
								/>
								{lang.t("Repost the job to all eligible officers")}
							</label>
						</div>
					</div>
				</If>
				<RadioWithTooltip
					id="call-off-1"
					name="call-off-options"
					handleChange={handleTypeChange(1)}
					checked={jobCallOffType === 1}
					label={lang.t(
						"Officers can call off through the app with no additional actions required",
					)}
					tooltipMessage={`
                    ${lang.t(
						"This will allow an officer to be able to remove themselves completely from a job with no additional actions required_",
					)}
                    ${lang.t("The job becomes available to be re-assigned to a new officer_")}
                    `}
				/>
			</div>
			<If condition={jobCallOffType === 2 || jobCallOffType === ""}>
				<div className="call-off-subsection">
					<h4 className="panel-title">
						{lang.t("Would you like to allow a call off grace period_")}
					</h4>
					<small className="text-muted">
						{lang.t(
							"Officers can call off of a job with no actions required after call off_",
						)}
					</small>
					<YesNoRadioButton
						id="has-grace-period"
						name="has-grace-period"
						label=""
						checked={hasGracePeriod}
						handleChange={handleHasGracePeriodChange}
					/>
					<If condition={hasGracePeriod}>
						<div className="grace-period-container">
							<h4
								className="text-muted"
								style={{ fontSize: "14px" }}
							>
								{lang.t("Please set a call off grace period")}
							</h4>
							<h4
								className="panel-title call-off-limit-group"
								style={{ fontSize: "14px" }}
							>
								{lang.t(
									"Officers will be unassigned from a job if the call off is made more than",
								)}
								<span className="call-off-limit-input-group">
									<div id="call-off-range-section">
										<div className="call-off-range-input col-xs-4 form-group">
											<input
												type="tel"
												className="form-control"
												value={currentGraceTime}
												onChange={graceTimeChanged}
											/>
										</div>
										<div className="call-off-range-select col-xs-8 form-group">
											<select
												id="call-off-expiration"
												name="call-off-expiration"
												className="custom-select form-control"
												onChange={unitOfTimeGracePeriodChanged}
												value={selectedGraceTimePeriod}
											>
												{timePeriods.map(item => (
													<option
														key={item.label}
														value={item.factor}
													>
														{item.label}
													</option>
												))}
											</select>
										</div>
									</div>
								</span>
								{lang.t("from the start of the job_")}
							</h4>
						</div>
					</If>
				</div>
			</If>
			<If condition={jobCallOffType !== ""}>
				<div
					className="call-off-subsection"
					style={{ display: "flex" }}
				>
					<h4
						className="panel-title call-off-limit-group"
						style={{ fontSize: "14px" }}
					>
						{lang.t("Call offs are no longer allowed")}
						<span className="call-off-limit-input-group">
							<div id="call-off-range-section">
								<div className="call-off-range-input col-xs-4 form-group">
									<input
										type="tel"
										className={
											currentTime * selectedPeriod >
												currentGraceTime * selectedGraceTimePeriod &&
											hasGracePeriod &&
											(jobCallOffType === 2 || jobCallOffType === "")
												? "form-control invalid-data"
												: "form-control"
										}
										value={currentTime}
										onChange={expirationTimeChanged}
									/>
								</div>
								<div className="call-off-range-select col-xs-8 form-group">
									<select
										id="call-off-expiration"
										name="call-off-expiration"
										className="custom-select form-control"
										onChange={unitOfTimeChanged}
										value={selectedPeriod}
									>
										{timePeriods.map(item => (
											<option
												key={item.label}
												value={item.factor}
											>
												{item.label}
											</option>
										))}
									</select>
								</div>
							</div>
						</span>
						{lang.t("or less from the start of the job_")}
					</h4>
				</div>
			</If>
		</SettingsCard>
	);
};

OfficerCallOffSection.propTypes = {
	handleExpirationChange: PropTypes.func.isRequired,
	handleTypeChange: PropTypes.func.isRequired,
	handleReplacementTypeChange: PropTypes.func.isRequired,
	handleGracePeriodChange: PropTypes.func.isRequired,
	id: PropTypes.string.isRequired,
	sectionRef: PropTypes.any.isRequired,
	title: PropTypes.string.isRequired,
	subtitle: PropTypes.string.isRequired,
	jobCallOffType: PropTypes.any.isRequired,
	jobCallOffInitialTime: PropTypes.any.isRequired,
	gracePeriodInitialTime: PropTypes.any.isRequired,
	jobCallOffReplacementTypes: PropTypes.any.isRequired,
	hasGracePeriod: PropTypes.bool,
	handleHasGracePeriodChange: PropTypes.func.isRequired,
};
OfficerCallOffSection.defaultProps = {
	hasGracePeriod: false,
};
export default OfficerCallOffSection;
